import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import juice from 'juice'
import { useTheme } from '@emotion/react'
import { FindOneEmailTemplate, FindOneEmailTemplateVariables } from '__generatedTypes__/FindOneEmailTemplate'
import { SendEmail, SendEmailVariables } from '__generatedTypes__/SendEmail'
import { UpdateEmailTemplate, UpdateEmailTemplateVariables } from '__generatedTypes__/UpdateEmailTemplate'
import { EmailTemplateTypeEnum } from '__generatedTypes__/globalTypes'
import { DAT_BOOSTER_EMAIL } from 'constants/consts'
import { FIND_ONE_EMAIL_TEMPLATE, SEND_EMAIL, UPDATE_EMAIL_TEMPLATE } from 'queries/emails'
import { AppEnvs } from 'types/envs'
import { getCurrentUserEmail } from 'utils/auth'
import { getAsHtmlBaseStyles } from './emailStyles'

const removeNotAllowedChars = (html: string) =>
  // eslint-disable-next-line no-misleading-character-class
  html.replace(/[\u2000-\u200F\u2028-\u202F\u205F-\u206F\uFEFF]/g, '')

type UpdateVariables = UpdateEmailTemplateVariables['updateEmailTemplateInputFieldName']

export const useEmailTemplate = (type: EmailTemplateTypeEnum, platformId?: string) => {
  const theme = useTheme()

  const emailTemplateVariables = {
    platform: platformId ? { id: platformId } : undefined,
    type,
  }

  const {
    data,
    loading: templateLoading,
    refetch,
  } = useQuery<FindOneEmailTemplate, FindOneEmailTemplateVariables>(FIND_ONE_EMAIL_TEMPLATE, {
    variables: emailTemplateVariables,
  })

  const [update, { loading: updateTemplateLoading }] = useMutation<UpdateEmailTemplate, UpdateEmailTemplateVariables>(
    UPDATE_EMAIL_TEMPLATE,
    {
      variables: {
        updateEmailTemplateInputFieldName: emailTemplateVariables,
      },
    }
  )

  const [send, { loading: sendEmailLoading }] = useLazyQuery<SendEmail, SendEmailVariables>(SEND_EMAIL, {
    fetchPolicy: 'network-only',
  })

  const updateTemplate = (variables: UpdateVariables) => {
    const withInlineStyles = juice(removeNotAllowedChars(variables.emailAsHtml || ''), {
      extraCss: getAsHtmlBaseStyles(theme).styles,
      preserveFontFaces: false,
      preserveKeyFrames: false,
      insertPreservedExtraCss: false,
    })

    update({
      variables: {
        updateEmailTemplateInputFieldName: {
          ...emailTemplateVariables,
          ...variables,
          emailAsHtml: withInlineStyles,
        },
      },
    })
  }

  const sendEmail = (values?: unknown) => async () => {
    if (!template) return

    const adminEmail = (await getCurrentUserEmail()) || ''

    const isSandboxOrProd = [AppEnvs.SANDBOX, AppEnvs.PROD].includes(process.env.REACT_APP_ENV)

    send({
      variables: {
        input: {
          data: values ? JSON.stringify(values) : null,
          templateName: template.title,
          email: isSandboxOrProd ? [DAT_BOOSTER_EMAIL] : [adminEmail],
        },
      },
    })
  }

  const template = data?.findOneEmailTemplate

  return {
    template,
    refetchTemplate: refetch,
    updateTemplate,
    templateLoading,
    updateTemplateLoading,
    sendEmail,
    sendEmailLoading,
  }
}
